'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Link, usePathname, useRouter } from '@/i18n/routing';
import { cn } from '@/shared/utils/cn';
import { pagesRoutes } from '@/shared/constants/routes';
import { CALLBACK_URL_PARAM } from '@/shared/constants/search-params';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { GameCardUIStar } from '@/features/games/shared/components/game-card-ui-star';

const STAR_VALUES = [1, 2, 3, 4, 5] as const;
const STAR_COUNT = STAR_VALUES.length;

type ReviewGetResponse = { userRating: number | null; error?: string };
type ReviewResponse = { userRating: number; error?: string; details?: string[] };

type GameRatingProps = {
  initialRating?: number;
  ratingCount?: number;
  gameId: number;
  className?: string;
};

function starFillPctFor(starIndex1Based: number, fractional: number): number {
  const v = Math.max(0, Math.min(5, fractional));
  const portion = Math.max(0, Math.min(1, v - (starIndex1Based - 1)));
  return Math.round(portion * 100);
}

function clampAvg(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(STAR_COUNT, value));
}

function normalizeUserRatingToInt(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > STAR_COUNT) return STAR_COUNT;
  return Math.floor(value);
}

export const GameRating = ({
  initialRating = 0,
  ratingCount = 0,
  gameId,
  className,
}: GameRatingProps) => {
  const t = useTranslations('GamePage');
  const router = useRouter();
  const pathname = usePathname();
  const { authenticatedUser } = useUser();
  const isAuthenticated = Boolean(authenticatedUser);

  const [rating, setRating] = useState(normalizeUserRatingToInt(initialRating));
  const [avg, setAvg] = useState<number>(initialRating);
  const [count, setCount] = useState(ratingCount ?? 0);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasRated, setHasRated] = useState<boolean>(true);
  const [isFetchingRating, setIsFetchingRating] = useState<boolean>(false);

  const qs = typeof window !== 'undefined' ? window.location.search : '';
  const callbackUrl = qs ? `${pathname}${qs}` : pathname;
  const loginHref = `${pagesRoutes.login.href}?${CALLBACK_URL_PARAM}=${encodeURIComponent(callbackUrl)}`;

  const visualValue = hoverRating ?? clampAvg(avg);

  useEffect(() => {
    if (!isAuthenticated) return;
    const productEntityId = Number(gameId);
    if (!productEntityId || Number.isNaN(productEntityId)) return;
    let isActive = true;

    const fetchUserRating = async () => {
      setIsFetchingRating(true);
      try {
        const res = await fetch(`/api/game/${productEntityId}/review`, {
          method: 'GET',
          cache: 'no-store',
        });
        if (!isActive || !res.ok) return;
        const data: ReviewGetResponse = (await res.json()) as ReviewGetResponse;
        const value = typeof data.userRating === 'number' ? data.userRating : null;
        setHasRated(value !== null);
        if (value !== null) {
          setRating(normalizeUserRatingToInt(value));
        }
      } finally {
        if (isActive) setIsFetchingRating(false);
      }
    };
    void fetchUserRating();
    return () => {
      isActive = false;
    };
  }, [gameId, isAuthenticated]);

  const submitRating = async (value: number): Promise<void> => {
    const productEntityId = Number(gameId);
    if (!productEntityId || Number.isNaN(productEntityId)) return;

    const res = await fetch(`/api/game/${productEntityId}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rating: value }),
      cache: 'no-store',
    });
    if (!res.ok) return;

    const data: ReviewResponse = (await res.json()) as ReviewResponse;
    if (typeof data.userRating !== 'number') return;

    const nextUserRating = normalizeUserRatingToInt(data.userRating);
    const hadRatedBefore = hasRated === true;
    const prevUserRating = hadRatedBefore ? rating : null;
    setRating(nextUserRating);

    setCount((prevCount) => {
      const nextCount = hadRatedBefore ? prevCount : prevCount + 1;
      setAvg((prevAvg) => {
        if (hadRatedBefore) {
          if (prevCount > 0) {
            return (prevAvg * prevCount - (prevUserRating ?? 0) + nextUserRating) / prevCount;
          }
          return nextUserRating;
        }
        return (prevAvg * prevCount + nextUserRating) / nextCount;
      });
      setHasRated(true);
      return nextCount;
    });
  };

  const handleClick = async (value: number): Promise<void> => {
    if (!isAuthenticated) {
      router.push(loginHref);
      return;
    }
    if (isSubmitting || isFetchingRating) return;
    setHoverRating(null);
    setIsSubmitting(true);
    try {
      await submitRating(value);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return <div className={cn('h-5', className)} aria-busy aria-live='polite' />;
  }

  if (!isAuthenticated) {
    return (
      <Link
        href={pagesRoutes.login.href}
        onClick={(e) => {
          e.preventDefault();
          router.push(loginHref);
        }}
        className={cn('group flex items-center gap-2 text-white', className)}
        aria-label={t('gameRating')}
      >
        <div className='flex items-center gap-1'>
          {STAR_VALUES.map((value) => (
            <GameCardUIStar key={value} fillPct={starFillPctFor(value, visualValue)} size={20} />
          ))}
        </div>
        <span className='sr-only'>{`${t('gameRating')}: ${visualValue.toFixed(1)} / ${STAR_COUNT}. (${count})`}</span>
        <span className='text-sm font-semibold'>({count})</span>
      </Link>
    );
  }

  return (
    <div className={cn('flex items-center gap-2 text-white', className)}>
      <span className='sr-only' aria-live='polite'>
        {`${t('gameRating')}: ${visualValue.toFixed(1)} / ${STAR_COUNT}. (${count})`}
      </span>

      <div className='flex items-center gap-1' onMouseLeave={() => setHoverRating(null)}>
        {STAR_VALUES.map((value) => (
          <button
            key={value}
            type='button'
            aria-label={`${t('rate')} ${value} ${value === 1 ? t('star') : t('stars')}`}
            tabIndex={-1}
            onMouseEnter={() => setHoverRating(value)}
            onClick={() => handleClick(value)}
            disabled={isSubmitting}
            className={cn(
              'inline-flex items-center justify-center rounded-xs bg-transparent p-0 outline-hidden',
              'h-5 w-5',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]',
              'disabled:cursor-not-allowed',
            )}
          >
            <GameCardUIStar fillPct={starFillPctFor(value, visualValue)} size={20} />
          </button>
        ))}
      </div>

      <span className='text-sm font-semibold'>({count})</span>
    </div>
  );
};
